import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import ClearIcon from '@material-ui/icons/Clear';
import { SnackbarContext } from 'components';
import * as React from 'react';
import {
  LightspeedCatalogItem,
  LightspeedMappingService,
  LightspeedShopId,
  LightspeedShopState,
  LightspeedStateSetMenu,
} from 'services';

interface Props {
  shopId: LightspeedShopId;
  state: LightspeedShopState | null;
  itemsCatalog: LightspeedCatalogItem[];
  onChanged: () => void | Promise<void>;
}

interface CatalogOption {
  sku: string;
  label: string;
}

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    '& > * + *': { marginLeft: theme.spacing(2) },
  },
  search: { minWidth: 280 },
  emptyHint: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
  },
  ok: { background: '#e8f5e9' },
  drift: { background: '#ffebee' },
}));

export const SetMenusTab: React.FC<Props> = ({
  shopId,
  state,
  itemsCatalog,
  onChanged,
}) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);

  const [filter, setFilter] = React.useState('');
  const [savingId, setSavingId] = React.useState<number | null>(null);

  const options: CatalogOption[] = React.useMemo(
    () =>
      itemsCatalog
        .filter((c): c is LightspeedCatalogItem & { sku: string } =>
          typeof c.sku === 'string' && c.sku !== '',
        )
        .map((c) => ({
          sku: String(c.sku),
          label:
            (typeof c.name === 'string' && c.name !== ''
              ? c.name
              : '(no name)') + ` · SKU ${c.sku}`,
        })),
    [itemsCatalog],
  );

  const validSkus = React.useMemo(
    () => new Set(options.map((o) => o.sku)),
    [options],
  );

  const filtered = React.useMemo(() => {
    if (!state) return [];
    const q = filter.trim().toLowerCase();
    return state.setMenus.filter(
      (sm) => q === '' || sm.name.toLowerCase().includes(q),
    );
  }, [state, filter]);

  async function saveSku(sm: LightspeedStateSetMenu, sku: string | null) {
    try {
      setSavingId(sm.id);
      await LightspeedMappingService.updateSetMenu(sm.id, {
        shopId,
        lightspeed_sku: sku,
      });
      snackbar.success(
        sku ? `Linked "${sm.name}" → SKU ${sku}` : `Cleared "${sm.name}"`,
      );
      await onChanged();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setSavingId(null);
    }
  }

  if (!state) {
    return (
      <Typography className={classes.emptyHint}>
        Loading set menus for shop {shopId}…
      </Typography>
    );
  }

  const catalogReady = itemsCatalog.length > 0;

  return (
    <Box>
      <Box className={classes.toolbar}>
        <TextField
          variant="outlined"
          size="small"
          className={classes.search}
          placeholder="Search set menus…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Box flex={1} />
        <Typography variant="body2" color="textSecondary">
          {state.setMenus.length} set menus in shop {shopId}
        </Typography>
      </Box>

      {!catalogReady && (
        <Paper className={classes.emptyHint} elevation={0}>
          No items catalog has been pasted for this shop yet. Set menus
          are mapped to Lightspeed SKUs from the items catalog. Use the{' '}
          <strong>Paste catalogs</strong> tab first.
        </Paper>
      )}

      {catalogReady && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '35%' }}>Set menu</TableCell>
                <TableCell style={{ width: '15%' }}>Price</TableCell>
                <TableCell style={{ width: '10%' }}>Status</TableCell>
                <TableCell>Lightspeed SKU</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((sm) => {
                const currentSku = sm.lightspeedSku;
                const driftBadge =
                  currentSku && !validSkus.has(currentSku) ? (
                    <Chip
                      size="small"
                      label="Drift — SKU not in catalog"
                      className={classes.drift}
                    />
                  ) : currentSku ? (
                    <Chip size="small" label="Mapped" className={classes.ok} />
                  ) : (
                    <Chip size="small" label="Unmapped" />
                  );

                const selectedOpt = currentSku
                  ? options.find((o) => o.sku === currentSku) ?? {
                      sku: currentSku,
                      label: `${currentSku} (not in latest catalog)`,
                    }
                  : null;

                return (
                  <TableRow key={sm.id}>
                    <TableCell>
                      <strong>{sm.name}</strong>
                      <br />
                      <Typography variant="caption" color="textSecondary">
                        id {sm.id}
                      </Typography>
                    </TableCell>
                    <TableCell>€{Number(sm.price).toFixed(2)}</TableCell>
                    <TableCell>{driftBadge}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Autocomplete
                          style={{ flex: 1 }}
                          size="small"
                          options={options}
                          getOptionLabel={(o) => o.label}
                          getOptionSelected={(a, b) => a.sku === b.sku}
                          value={selectedOpt}
                          disabled={savingId === sm.id}
                          onChange={(_e: any, picked: CatalogOption | null) => {
                            const sku = picked ? picked.sku : null;
                            if (sku === currentSku) return;
                            saveSku(sm, sku);
                          }}
                          renderInput={(p) => (
                            <TextField
                              {...p}
                              variant="outlined"
                              placeholder="Pick a Lightspeed SKU"
                            />
                          )}
                        />
                        {currentSku && (
                          <IconButton
                            size="small"
                            disabled={savingId === sm.id}
                            onClick={() => saveSku(sm, null)}
                            aria-label="Clear mapping"
                          >
                            <ClearIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography className={classes.emptyHint}>
                      No set menus match the current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};
